import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';

import { Accordion, AccordionTitle, AccordionContent } from '@hpi-sox/hpstellar-core';

import { SearchContext } from './search-context';
import AccessibleElement from '../../ui/components/accessible-element';
import Icon from '../../ui/components/icon';
import Tooltip from '../../shared/components/tooltip';
import { Helpers } from '../../core/src/helpers';

import '../css/search-filter.less';

const getOptionsWithCanonical = (field, options, getFilterUrl = v => v) => {
    return options.map(option => {
        const { alias, value } = option;
        const url = getFilterUrl({ [`facets.${field}`]: [alias || value?.text] }, {});
        return {
            ...option,
            canonical: url,
        };
    });
};

/**
Context provider for search facet filter selection
@examples
```jsx
<SearchFilter
    displayName={displayName}
    options={facetValues}
    field={useAlias ? alias : key}
    metricsId={metricsId}
    CustomFilterOption={(props) => (
        <CustomFilterOptions useAlias={useAlias} {...props} />
    )}
    onFilterClick={closeModal}
    defaultExpanded={defaultExpanded}
/>
```
@component SearchFilter
@import SearchFilter
@example ../docs/SearchFilter.md
@returns {ReactElement} The rendered component
*/
function SearchFilter(props) {
    const {
        displayName,
        field,
        operator,
        id,
        size,
        options,
        CustomFilterOption,
        metricsId,
        description,
        onFilterClick,
        defaultExpanded = false,
    } = props;
    const [expand, setExpand] = useState(defaultExpanded);
    const [lastFocusedId, setLastFocusedId] = useState(null);
    const context = useContext(SearchContext);
    const { updateQuery, aggregations, customContext, getFilterUrl } = context || {};
    const isV2 = (customContext && customContext.isV2) || false;

    const toggleMenu = () => setExpand(e => !e);

    if (isV2) {
        return (
            <Accordion className="accordion" defaultExpanded={expand} size="small">
                <AccordionTitle
                    tooltipPosition={'right'}
                    tooltip={
                        description && {
                            description: <span dangerouslySetInnerHTML={Helpers.createMarkup(description)} />,
                        }
                    }
                    title={displayName}
                />
                <AccordionContent className="accordionContent">
                    {CustomFilterOption && (
                        <CustomFilterOption
                            options={getOptionsWithCanonical(field, options, getFilterUrl)}
                            filterName={displayName}
                            field={field}
                            onClick={updateQuery}
                            onFilterClick={onFilterClick}
                            metricsId={metricsId}
                            lastFocusedId={lastFocusedId}
                            setLastFocusedId={setLastFocusedId}
                        />
                    )}
                </AccordionContent>
            </Accordion>
        );
    }

    return (
        <div className={`filter-group${expand ? ' expanded' : ' closed'}`}>
            <AccessibleElement element="h3" className="filter-header" ariaLabel={displayName} onClick={toggleMenu}>
                {description && (
                    <Tooltip ariaLabel={description} displayOn="click" linkId={`filter:${displayName}`}>
                        {description}
                    </Tooltip>
                )}
                {displayName}
                <span className="arrow"></span>
                <Icon name={expand ? 'minus_m' : 'plus'} size={20} />
            </AccessibleElement>
            <ul className="facet-values">
                {CustomFilterOption && (
                    <CustomFilterOption
                        options={getOptionsWithCanonical(field, options, getFilterUrl)}
                        filterName={displayName}
                        field={field}
                        onClick={updateQuery}
                        onFilterClick={onFilterClick}
                        metricsId={metricsId}
                    />
                )}
            </ul>
        </div>
    );
}

SearchFilter.defaultProps = {
    defaultExpanded: false,
};

SearchFilter.propTypes = {
    defaultExpanded: PropTypes.bool,
    CustomFilterOption: PropTypes.func,
    options: PropTypes.array,
};

export default SearchFilter;
